import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription, DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { ROLES } from "../lib/constants";
import { toast } from "sonner";
import {
  Copy, ArchiveRestore, Trash2, ShieldAlert, Users as UsersIcon,
  Heart, Mail, AlertTriangle, RotateCcw, KeyRound,
} from "lucide-react";

const STAFF_ROLES = new Set(["admin", "service", "vask"]);

// ---- Reset password dialog -------------------------------------------------
function ResetPasswordDialog({ targetUser, onDone }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (pw.length < 8) return toast.error("Minst 8 tegn");
    if (pw !== confirm) return toast.error("Passordene er ikke like");
    setBusy(true);
    try {
      await api.post(`/users/${targetUser.id}/reset-password`, { new_password: pw });
      toast.success(`Nytt passord satt for ${targetUser.name}. Husk å gi det videre.`);
      setOpen(false);
      setPw("");
      setConfirm("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke endre passord");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-[#BD984D] text-[#A57F39] hover:bg-[#BD984D]/10"
          data-testid={`user-reset-pw-${targetUser.id}`}
        >
          <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Bytt passord
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bytt passord for {targetUser.name}</DialogTitle>
          <DialogDescription>
            Sett et nytt passord for denne brukeren. Du må selv gi det videre på
            en sikker måte. Brukeren kan endre det selv etter første innlogging.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nytt passord</Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid={`user-reset-pw-new-${targetUser.id}`}
              placeholder="Minst 8 tegn"
            />
          </div>
          <div>
            <Label>Bekreft</Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid={`user-reset-pw-confirm-${targetUser.id}`}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Avbryt</Button>
          <Button
            onClick={submit}
            disabled={busy || !pw || !confirm}
            data-testid={`user-reset-pw-submit-${targetUser.id}`}
            className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
          >
            Sett passord
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Two-step archive dialog -----------------------------------------------
function ArchiveDialog({ targetUser, onArchived }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => { setOpen(false); setTimeout(() => { setStep(1); setReason(""); }, 200); };

  const archive = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${targetUser.id}`, {
        data: { confirm_twice: true, reason: reason.trim() },
      });
      toast.success("Brukeren er arkivert i 30 dager");
      onArchived();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke arkivere");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { v ? setOpen(true) : close(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm"
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid={`user-archive-${targetUser.id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Arkiver
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            {step === 1 ? "Bekreft arkivering" : "Bekreft én gang til"}
          </DialogTitle>
          <DialogDescription>
            {step === 1 ? (
              <>Brukeren <strong>{targetUser.name}</strong> ({targetUser.email}) vil bli
              arkivert og <strong>permanent slettet etter 30 dager</strong>. I denne
              perioden kan du enkelt gjenopprette kontoen.</>
            ) : (
              <>Er du <strong>helt sikker</strong>? Brukeren vil ikke kunne logge inn
              fra og med nå. Trykk «Bekreft arkivering» for å fortsette.</>
            )}
          </DialogDescription>
        </DialogHeader>
        {step === 1 && (
          <div className="mt-2">
            <Label className="text-xs">Årsak (valgfri)</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="F.eks. «Sluttet i jobben»"
              data-testid={`user-archive-reason-${targetUser.id}`}
            />
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={close}>Avbryt</Button>
          {step === 1 ? (
            <Button onClick={() => setStep(2)}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    data-testid={`user-archive-confirm1-${targetUser.id}`}>
              Fortsett
            </Button>
          ) : (
            <Button onClick={archive} disabled={busy}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid={`user-archive-confirm2-${targetUser.id}`}>
              Bekreft arkivering
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Permanent delete dialog (with typed phrase) ---------------------------
function PermanentDeleteDialog({ targetUser, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const phraseOk = phrase === "SLETT PERMANENT";

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${targetUser.id}/permanent-delete`, {
        confirm_text: phrase,
      });
      toast.success("Brukeren er slettet permanent");
      onDeleted();
      setOpen(false);
      setPhrase("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm"
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid={`user-perm-delete-${targetUser.id}`}>
          Slett permanent
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" /> Permanent sletting
          </DialogTitle>
          <DialogDescription>
            Dette kan <strong>ikke angres</strong>. Brukeren og alle relaterte varsler vil
            fjernes umiddelbart. Skriv <code className="px-1 py-0.5 bg-slate-100 rounded text-red-600">SLETT PERMANENT</code> for å bekrefte.
          </DialogDescription>
        </DialogHeader>
        <Input value={phrase} onChange={(e) => setPhrase(e.target.value)}
               placeholder="SLETT PERMANENT"
               data-testid={`user-perm-phrase-${targetUser.id}`} />
        <DialogFooter>
          <Button onClick={submit} disabled={!phraseOk || busy}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                  data-testid={`user-perm-confirm-${targetUser.id}`}>
            Slett permanent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- User row --------------------------------------------------------------
function UserRow({ u, currentUser, archived, onRestore, onChanged }) {
  return (
    <div className={`px-4 py-3 flex items-center justify-between gap-3 flex-wrap ${
      archived ? "bg-slate-50" : ""
    }`} data-testid={`user-row-${u.id}`}>
      <div className="min-w-0 flex-1">
        <div className={`font-medium text-[#0C1824] flex items-center gap-2 ${
          archived ? "line-through opacity-70" : ""
        }`}>
          {u.name}
          {u.is_super_admin && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#BD984D] text-[#0C1824] text-[10px] font-bold uppercase tracking-wider">
              Hovedadmin
            </span>
          )}
        </div>
        <div className="text-xs text-[#2B3F4D]">{u.email}</div>
        {archived && (
          <div className="text-[11px] text-[#A57F39] mt-0.5">
            Arkivert {u.archived_at ? new Date(u.archived_at).toLocaleDateString("nb-NO") : ""}
            {u.archived_by_name && ` · av ${u.archived_by_name}`}
            {u.archive_reason && ` · «${u.archive_reason}»`}
            {u.purge_after && (
              <span className="text-red-600 ml-1.5 font-mono">
                · slettes {new Date(u.purge_after).toLocaleDateString("nb-NO")}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Badge className="bg-[#0C1824] text-[#ECC974]">{ROLES[u.role]}</Badge>
        {archived ? (
          <>
            <Button variant="outline" size="sm" onClick={() => onRestore(u)}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid={`user-restore-${u.id}`}>
              <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" /> Gjenopprett
            </Button>
            {currentUser?.is_super_admin && !u.is_super_admin && (
              <PermanentDeleteDialog targetUser={u} onDeleted={onChanged} />
            )}
          </>
        ) : (
          <>
            {!u.is_super_admin && u.id !== currentUser?.id && (
              <ResetPasswordDialog targetUser={u} onDone={onChanged} />
            )}
            {currentUser?.is_super_admin && !u.is_super_admin && u.id !== currentUser.id && (
              <ArchiveDialog targetUser={u} onArchived={onChanged} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---- Main page -------------------------------------------------------------
export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "service", booking_id: "" });
  const [lastInvite, setLastInvite] = useState(null);

  const load = async () => {
    const [u, i, b] = await Promise.all([
      api.get("/users"),
      api.get("/invites"),
      api.get("/bookings"),
    ]);
    setUsers(u.data);
    setInvites(i.data);
    setBookings(b.data);
  };

  useEffect(() => { load(); }, []);

  const onInvite = async () => {
    try {
      const payload = { ...form };
      if (!payload.booking_id) delete payload.booking_id;
      const { data } = await api.post("/invites", payload);
      setLastInvite(data);
      setForm({ email: "", name: "", role: "service", booking_id: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke invitere");
    }
  };

  const onDeleteInvite = async (id) => {
    await api.delete(`/invites/${id}`);
    load();
  };

  const onRestore = async (u) => {
    try {
      await api.post(`/users/${u.id}/restore`);
      toast.success("Brukeren er gjenopprettet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gjenopprette");
    }
  };

  const runMaintenance = async () => {
    try {
      await api.post("/users/maintenance/run");
      toast.success("Rydding kjørt – arkiverte ferdige kunder & slettet utløpte");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke kjøre rydding");
    }
  };

  const inviteLink = (token) => `${window.location.origin}/accept-invite?token=${token}`;

  const copy = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Kopiert");
        return;
      }
      throw new Error("no clipboard api");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success("Kopiert");
        else toast.error("Kunne ikke kopiere");
      } catch {
        toast.error("Kunne ikke kopiere");
      }
    }
  };

  const partition = useMemo(() => {
    const staffActive = [], staffArchived = [], kundeActive = [], kundeArchived = [];
    for (const u of users) {
      const arr = STAFF_ROLES.has(u.role)
        ? (u.is_archived ? staffArchived : staffActive)
        : (u.is_archived ? kundeArchived : kundeActive);
      arr.push(u);
    }
    return { staffActive, staffArchived, kundeActive, kundeArchived };
  }, [users]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">Tilgang</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">Brukere</h1>
          {!currentUser?.is_super_admin && (
            <p className="text-sm text-[#2B3F4D] mt-1">
              Bare hovedadmin kan arkivere og slette brukere. Du kan invitere nye.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {currentUser?.is_super_admin && (
            <Button variant="outline" onClick={runMaintenance}
                    data-testid="users-run-maintenance"
                    className="border-[#1DA3B3] text-[#1DA3B3] hover:bg-[#1DA3B3]/10">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Kjør opprydding
            </Button>
          )}
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setLastInvite(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="invite-open" className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
                + Inviter bruker
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Inviter ny bruker</DialogTitle>
                <DialogDescription>
                  Generer en engangs invitasjonslenke og send til personen.
                </DialogDescription>
              </DialogHeader>
              {!lastInvite ? (
                <>
                  <div className="space-y-3">
                    <div>
                      <Label>E-post</Label>
                      <Input type="email" data-testid="invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div>
                      <Label>Navn</Label>
                      <Input data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div>
                      <Label>Rolle</Label>
                      <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                        <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="service">Service</SelectItem>
                          <SelectItem value="vask">Vask</SelectItem>
                          <SelectItem value="kunde">Kunde</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {form.role === "kunde" && (
                      <div>
                        <Label>Koble til booking (valgfri)</Label>
                        <Select value={form.booking_id} onValueChange={(v) => setForm({ ...form, booking_id: v })}>
                          <SelectTrigger data-testid="invite-booking"><SelectValue placeholder="Velg booking" /></SelectTrigger>
                          <SelectContent>
                            {bookings.map((b) => (
                              <SelectItem key={b.id} value={b.id}>
                                {b.customer_name} · {b.start_date?.slice(0,10)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button onClick={onInvite} data-testid="invite-submit" className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
                      Lag invitasjon
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-[#2B3F4D]">
                    Send denne lenken til <b>{lastInvite.email}</b>:
                  </p>
                  <Input readOnly value={inviteLink(lastInvite.token)}
                         onFocus={(e) => e.target.select()} className="text-xs"
                         data-testid="invite-link-input" />
                  <Button variant="outline"
                          onClick={() => copy(inviteLink(lastInvite.token))}
                          data-testid="invite-copy-link"
                          className="border-[#0C1824] text-[#0C1824] w-full">
                    <Copy className="w-4 h-4 mr-2" /> Kopier lenke
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="staff">
        <div className="overflow-x-auto -mx-1 pb-1">
          <TabsList className="w-max min-w-full">
            <TabsTrigger value="staff" data-testid="users-tab-staff">
            <UsersIcon className="w-4 h-4 mr-1.5" />
            Ansatte ({partition.staffActive.length})
          </TabsTrigger>
          <TabsTrigger value="kunder" data-testid="users-tab-kunder">
            <Heart className="w-4 h-4 mr-1.5" />
            Kunder ({partition.kundeActive.length})
          </TabsTrigger>
          <TabsTrigger value="invites" data-testid="users-tab-invites">
            <Mail className="w-4 h-4 mr-1.5" />
            Invitasjoner ({invites.length})
          </TabsTrigger>
          </TabsList>
        </div>

        {/* STAFF TAB */}
        <TabsContent value="staff" className="mt-5 space-y-4">
          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-[#E2E8F0] font-semibold text-[#0C1824] bg-[#0C1824]/[0.02]">
              Aktive ansatte
            </div>
            <div className="divide-y divide-[#E2E8F0]">
              {partition.staffActive.length === 0 && (
                <div className="px-4 py-6 text-sm text-[#2B3F4D]">Ingen aktive ansatte.</div>
              )}
              {partition.staffActive.map((u) => (
                <UserRow key={u.id} u={u} currentUser={currentUser}
                         archived={false} onRestore={onRestore} onChanged={load} />
              ))}
            </div>
          </Card>
          {partition.staffArchived.length > 0 && (
            <Card className="overflow-hidden border-amber-200">
              <div className="px-4 py-3 border-b border-amber-200 bg-amber-50 text-amber-900 font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Arkiverte ansatte ({partition.staffArchived.length})
                <span className="text-[11px] font-normal text-amber-700 ml-2">
                  · Slettes permanent etter 30 dager
                </span>
              </div>
              <div className="divide-y divide-amber-100">
                {partition.staffArchived.map((u) => (
                  <UserRow key={u.id} u={u} currentUser={currentUser}
                           archived={true} onRestore={onRestore} onChanged={load} />
                ))}
              </div>
            </Card>
          )}
        </TabsContent>

        {/* CUSTOMERS TAB */}
        <TabsContent value="kunder" className="mt-5 space-y-4">
          <Card className="p-4 bg-gradient-to-br from-[#1DA3B3]/8 to-transparent border-[#1DA3B3]/20">
            <p className="text-sm text-[#2B3F4D]">
              Kundekontoer er midlertidige – de <strong>arkiveres automatisk</strong> etter utsjekk og
              <strong> gjenopprettes automatisk</strong> hvis kunden booker en ny tur med samme e-post.
              Du kan også manuelt gjenopprette dem fra arkivet under.
            </p>
          </Card>

          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-[#E2E8F0] font-semibold text-[#0C1824] bg-[#0C1824]/[0.02]">
              Aktive kunder
            </div>
            <div className="divide-y divide-[#E2E8F0]">
              {partition.kundeActive.length === 0 && (
                <div className="px-4 py-6 text-sm text-[#2B3F4D]">Ingen aktive kunder.</div>
              )}
              {partition.kundeActive.map((u) => (
                <UserRow key={u.id} u={u} currentUser={currentUser}
                         archived={false} onRestore={onRestore} onChanged={load} />
              ))}
            </div>
          </Card>

          {partition.kundeArchived.length > 0 && (
            <Card className="overflow-hidden">
              <div className="px-4 py-3 border-b border-[#E2E8F0] bg-slate-50 text-[#2B3F4D] font-semibold flex items-center gap-2">
                <Heart className="w-4 h-4 text-[#A57F39]" />
                Arkiverte kunder ({partition.kundeArchived.length})
                <span className="text-[11px] font-normal text-[#2B3F4D] ml-2">
                  · Auto-gjenopprettes ved ny booking
                </span>
              </div>
              <div className="divide-y divide-[#E2E8F0]">
                {partition.kundeArchived.map((u) => (
                  <UserRow key={u.id} u={u} currentUser={currentUser}
                           archived={true} onRestore={onRestore} onChanged={load} />
                ))}
              </div>
            </Card>
          )}
        </TabsContent>

        {/* INVITES TAB */}
        <TabsContent value="invites" className="mt-5">
          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-[#E2E8F0] font-semibold text-[#0C1824] bg-[#0C1824]/[0.02]">
              Invitasjoner
            </div>
            <div className="divide-y divide-[#E2E8F0]">
              {invites.length === 0 && <div className="px-4 py-6 text-sm text-[#2B3F4D]">Ingen invitasjoner.</div>}
              {invites.map((i) => (
                <div key={i.id} className="px-4 py-3 flex items-center justify-between flex-wrap gap-2"
                     data-testid={`invite-row-${i.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[#0C1824]">{i.name} · {i.email}</div>
                    <div className="text-xs text-[#2B3F4D] truncate">
                      {ROLES[i.role]} · {i.used ? "Brukt" : "Venter"} · utløper {new Date(i.expires_at).toLocaleDateString("nb-NO")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!i.used && (
                      <Button variant="outline" onClick={() => copy(inviteLink(i.token))} data-testid={`invite-copy-${i.id}`}>
                        <Copy className="w-4 h-4 mr-1" /> Kopier lenke
                      </Button>
                    )}
                    <Button variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => onDeleteInvite(i.id)}>
                      Slett
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
